export const createEmptyCondition = () => ({
  field: '',
  fields: [],
  operator: 'contains',
  value: '',
  ai_provider: ''
});

const uniqueTruthyStrings = (values) => {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map(value => String(value || '').trim())
    .filter(value => {
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

export const getAiConditionFields = (condition = {}) => {
  const fields = uniqueTruthyStrings(condition.fields);
  if (fields.length > 0) return fields;
  return uniqueTruthyStrings([condition.field]);
};

export const normalizeConditionForEditing = (condition = {}) => {
  const normalized = {
    ...createEmptyCondition(),
    ...condition
  };

  if (normalized.operator === 'ai_judge') {
    return {
      ...normalized,
      field: '',
      fields: getAiConditionFields(condition)
    };
  }

  return {
    ...normalized,
    field: condition.field || '',
    fields: []
  };
};

export const changeConditionOperator = (condition = {}, nextOperator = 'contains') => {
  const crossesAiBoundary =
    (condition.operator === 'ai_judge') !== (nextOperator === 'ai_judge');

  if (nextOperator === 'ai_judge') {
    return {
      ...condition,
      field: '',
      fields: getAiConditionFields(condition),
      operator: nextOperator,
      value: crossesAiBoundary ? '' : condition.value
    };
  }

  const aiFields = getAiConditionFields(condition);
  return {
    ...condition,
    field: condition.field || aiFields[0] || '',
    fields: [],
    operator: nextOperator,
    value: crossesAiBoundary ? '' : condition.value,
    ai_provider: ''
  };
};

export const isConditionComplete = (condition = {}) => {
  if (condition.operator === 'ai_judge') {
    return Boolean(
      getAiConditionFields(condition).length > 0 &&
      condition.ai_provider &&
      String(condition.value || '').trim()
    );
  }

  if (!condition.field) return false;
  if (condition.operator === 'is_empty' || condition.operator === 'is_not_empty') {
    return true;
  }
  return Boolean(String(condition.value || '').trim());
};

export const sanitizeCondition = (condition = {}) => {
  if (condition.operator === 'ai_judge') {
    return {
      fields: getAiConditionFields(condition),
      operator: 'ai_judge',
      value: condition.value || '',
      ai_provider: condition.ai_provider || '',
      ...(condition.ai_mode === 'judge_and_comment' ? {
        ai_mode: 'judge_and_comment',
        condition_id: condition.condition_id || condition._editorId || defaultEditorId('condition')
      } : {}),
      ...(condition.include_images === true ? { include_images: true } : {})
    };
  }

  const sanitized = {
    field: condition.field || '',
    operator: condition.operator || 'contains'
  };

  if (sanitized.operator !== 'is_empty' && sanitized.operator !== 'is_not_empty') {
    sanitized.value = condition.value || '';
  }

  return sanitized;
};

const defaultEditorId = (prefix) => {
  if (window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createEmptyConditionGroup = (
  idFactory = () => defaultEditorId('group')
) => ({
  _editorId: idFactory(),
  logic: 'and',
  conditions: []
});

const withEditorConditionId = (condition, idFactory) => ({
  ...normalizeConditionForEditing(condition),
  _editorId: idFactory()
});

export const normalizeConditionGroupsForEditing = (
  rawConditions,
  conditionLogic = 'and',
  idFactory = () => defaultEditorId('item')
) => {
  const items = Array.isArray(rawConditions) ? rawConditions : [];
  const isGrouped = items.length > 0 && items.every(
    item => item && Array.isArray(item.conditions)
  );
  const storedGroups = isGrouped
    ? items
    : [{ logic: conditionLogic || 'and', conditions: items }];

  return {
    conditionLogic: isGrouped ? (conditionLogic || 'and') : 'and',
    groups: storedGroups.map(group => ({
      _editorId: idFactory(),
      logic: group.logic === 'or' ? 'or' : 'and',
      conditions: (group.conditions || []).map(
        condition => withEditorConditionId(condition, idFactory)
      )
    }))
  };
};

export const sanitizeConditionGroups = (groups = []) => groups.map(group => ({
  logic: group.logic === 'or' ? 'or' : 'and',
  conditions: (group.conditions || []).map(sanitizeCondition)
}));

export const validateConditionGroups = (groups = []) => {
  if (!Array.isArray(groups) || groups.length === 0) {
    return '请至少添加一个条件组';
  }

  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const conditions = groups[groupIndex].conditions || [];
    if (conditions.length === 0) {
      return `条件组 ${groupIndex + 1} 至少需要一个条件`;
    }

    for (
      let conditionIndex = 0;
      conditionIndex < conditions.length;
      conditionIndex += 1
    ) {
      if (!isConditionComplete(conditions[conditionIndex])) {
        return `条件组 ${groupIndex + 1} 的条件 ${conditionIndex + 1} 不完整`;
      }
    }
  }

  return '';
};

export const copyConditionInGroups = (
  groups,
  groupId,
  conditionId,
  idFactory = () => defaultEditorId('condition')
) => groups.map(group => {
  if (group._editorId !== groupId) return group;

  const sourceIndex = group.conditions.findIndex(
    condition => condition._editorId === conditionId
  );
  if (sourceIndex < 0) return group;

  const source = group.conditions[sourceIndex];
  const copyId = idFactory();
  const copy = {
    ...source,
    ...(source.condition_id ? { condition_id: copyId } : {}),
    fields: Array.isArray(source.fields) ? [...source.fields] : source.fields,
    _editorId: copyId
  };
  const conditions = [...group.conditions];
  conditions.splice(sourceIndex + 1, 0, copy);
  return { ...group, conditions };
});

export const moveConditionInGroups = (
  groups,
  activeConditionId,
  targetGroupId,
  targetConditionId = null
) => {
  if (activeConditionId === targetConditionId) return groups;

  const targetGroupExists = groups.some(
    group => group._editorId === targetGroupId
  );
  const sourceGroup = groups.find(group => (
    group.conditions.some(
      condition => condition._editorId === activeConditionId
    )
  ));
  if (!sourceGroup || !targetGroupExists) return groups;

  const movingCondition = sourceGroup.conditions.find(
    condition => condition._editorId === activeConditionId
  );
  const withoutActive = groups.map(group => ({
    ...group,
    conditions: group.conditions.filter(
      condition => condition._editorId !== activeConditionId
    )
  }));

  return withoutActive.map(group => {
    if (group._editorId !== targetGroupId) return group;

    const conditions = [...group.conditions];
    const targetIndex = targetConditionId
      ? conditions.findIndex(item => item._editorId === targetConditionId)
      : -1;
    conditions.splice(
      targetIndex >= 0 ? targetIndex : conditions.length,
      0,
      movingCondition
    );
    return { ...group, conditions };
  });
};

export const getAiCommentConditionOptions = (groups = []) => groups.flatMap((group, groupIndex) => (
  (group.conditions || []).flatMap((condition, conditionIndex) => (
    condition.operator === 'ai_judge' && condition.ai_mode === 'judge_and_comment'
      ? [{ value: condition.condition_id, label: `条件组 ${groupIndex + 1} · 条件 ${conditionIndex + 1}：${condition.value || 'AI 判断'}` }]
      : []
  ))
));

export const validateAiCommentReferences = (groups, actions = []) => {
  const ids = new Set(getAiCommentConditionOptions(groups).map(option => option.value));
  return actions.some(action => action.type === 'add_comment' && action.comment_type === 'condition_result'
    && (!action.ai_condition_id || !ids.has(action.ai_condition_id)))
    ? '评论行为引用的 AI 条件不存在或未启用“判断并生成评论”，请重新选择' : '';
};
